<template>
  <Slideover :visible="visible">
    <div>
      <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
        <div class="flex items-start justify-between space-x-3">
          <h2 class="text-lg leading-7 font-medium">
            {{ title }}
          </h2>
          <div class="h-7 flex items-center">
            <button
              type="button"
              aria-label="Close panel"
              @click="$emit('toggle-slideover')"
              class="text-green-300 hover:text-white transition ease-in-out duration-150"
            >
              <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <!-- Main -->
      <div class="relative flex flex-1 flex-col py-3 px-2 mx-4 space-y-3">
        <div>
          <label
            :for="`purchase-note-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Purchase Note
          </label>
          <textarea
            v-model="purchaseNote"
            :id="`purchase-note-${_uid}`"
            rows="3"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
          ></textarea>
        </div>

        <div>
          <label
            :for="`care-note-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Care Note
          </label>
          <textarea
            v-model="careNote"
            :id="`care-note-${_uid}`"
            rows="3"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
          ></textarea>
        </div>

        <div class="flex items-center space-x-2">
          <span class="font-bold">Send Email</span>  
          <input type="checkbox" v-model="shouldSendEmail" />
        </div>
      </div>

      <div class="relative flex flex-1 flex-col px-2 mx-4 space-y-3" v-if="shouldSendEmail">
        <div class="grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6">
          <div :class="[updatingESD ? 'sm:col-span-3' : 'sm:col-span-6']">
            <div>
              <label
                :for="`action-${_uid}`"
                class="block text-sm font-medium leading-5 text-gray-700"
                >Action</label
              >
              <select
                :id="`action-${_uid}`"
                v-model="emailAction"
                class="mt-1 block form-select w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
              >
                <option selected value="none">
                  Do nothing to the order
                </option>
                <option value="hold">Put orders on hold</option>
                <option value="updateShipDate">
                  Update expected ship date
                </option>
              </select>
            </div>
          </div>
          <div class="sm:col-span-3" v-if="updatingESD">
            <div>
              <label
                :for="`esd-${_uid}`"
                class="block text-sm font-medium leading-5 text-gray-700"
                >Update ESD</label
              >
              <v-date-picker
                :id="`esd-${_uid}`"
                :popover="{ visibility: 'focus' }"
                class="block mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
                color="green"
                v-model="updateShipDate"
              />
            </div>
          </div>
        </div>

        <div>
          <label
            :for="`subject-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Subject
          </label>
          <input
            :disabled="true"
            :id="`subject-${_uid}`"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
            v-model="emailSubject"
          />
        </div>

        <div>
          <label
            :for="`note-${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Message
          </label>
          <textarea
            v-model="emailBody"
            :id="`message-${_uid}`"
            rows="3"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-not-allowed"
          ></textarea>
        </div>

        <i>Note: The request may take a couple of minutes to complete. Please do not retry until you have confirmed the emails did not get sent. You can check a customer's timeline in Gladly.</i>

      </div>

      <HttpError :error="error" dismissable />

      <!-- Footer -->
      <div class="flex-shrink-0 px-4 py-4 space-x-4 flex justify-end">
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="button"
            @click="$emit('toggle-slideover')"
            class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
          >
            Cancel
          </button>
        </span>
        <span class="inline-flex rounded-md shadow-sm">
          <button
            type="submit"
            class="inline-flex justify-center items-center space-x-2 py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green active:bg-green-700 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="processing || !canProcess"
            @click="process"
          >
            <span>Submit</span>
            <LoadingSpinner v-if="processing" />
          </button>
        </span>
      </div>
    </div>
  </Slideover>
</template>
<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Slideover from "@/components/layout/Slideover.vue";

const Utilities = ApiFactory.get("utilities");

export default {
  name: "AddNotes",
  components: {
    Slideover,
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    footer: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    orders: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      purchaseNote: null,
      careNote: null,
      error: null,
      processing: false,
      shouldSendEmail: false,
      emailAction: "none",
      updateShipDate: null,
      emailSubject: "Important information regarding your order",
      emailBody: '',
    };
  },
  computed: {
    canProcess() {
      if (this.shouldSendEmail && this.emailBody == '') {
        return false;
      }

      return this.purchaseNote || this.careNote;
    },
    updatingESD() {
      return this.emailAction == "updateShipDate";
    },
  },
  watch: {},
  created() {},
  mounted() {},
  methods: {
    process() {
      this.processing = true;
      this.error = null;

      const data = {
        items: this.orders.map((order) => {
          return {
            OrderNumber: order.OrderNumber,
            ItemNumber: order.ItemNumber,
            variant_id: order.variant_id,
          };
        }),
        purchaseNotes: this.purchaseNote,
        careNotes: this.careNote,
        send_bulk_emails: this.shouldSendEmail,
        bulk_email_request: {
          action: this.emailAction,
          body: this.emailBody,
          expected_ship_date: this.updateShipDate,
          customers: this.orders.map((order) => ({
            cart_id: parseInt(order.CartID),
            email: order.Email,
            order_number: parseInt(order.OrderNumber),
            item_number: parseInt(order.ItemNumber),
            store_front: order.StoreFront,
            source_order_id: parseInt(order.SourceOrderID),
            market_line_id: parseInt(order.MarketLineID)
          })),
        }
      };

      Utilities.createBackordersNotes(data)
        .then(() => {
          this.$emit("update-grid-notes", {
            purchaseNotes: this.purchaseNote,
            careNotes: this.careNote,
            updateShipDate: this.updateShipDate,
          });
          this.$emit("toggle-slideover");
        })
        .catch((error) => {
          this.error = error;
        })
        .finally(() => {
          this.processing = false;
        });
    },
  },
};
</script>
